import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const Availability = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [specificDates, setSpecificDates] = useState([]);
  const [timezone, setTimezone] = useState('Africa/Cairo');

  const daysArabic = {
    sunday: 'الأحد',
    monday: 'الإثنين',
    tuesday: 'الثلاثاء',
    wednesday: 'الأربعاء',
    thursday: 'الخميس',
    friday: 'الجمعة',
    saturday: 'السبت'
  };

  const daysEnglish = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  const timezones = [
    'Africa/Cairo',
    'Asia/Riyadh',
    'Asia/Kuwait',
    'Asia/Dubai',
    'Europe/London',
    'America/New_York',
    'Asia/Tokyo'
  ];

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/availability/agent/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setWeeklySchedule(data.data.weeklySchedule);
          setSpecificDates(data.data.specificDates || []);
          setTimezone(data.data.timezone || 'Africa/Cairo');
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/availability/agent/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          weeklySchedule,
          specificDates,
          timezone
        })
      });
      
      if (response.ok) {
        alert('✅ تم حفظ الجدول بنجاح!');
        fetchAvailability();
      } else {
        const data = await response.json();
        alert('❌ ' + (data.message || 'حدث خطأ'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('❌ حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  const addTimeSlot = (day) => {
    setWeeklySchedule(prev => {
      const currentSlots = prev[day] || [];
      // Only keep active slots
      const activeSlots = currentSlots.filter(s => s.is_active == 1);

      if (activeSlots.length === 0) {
        // ✅ FIX: Day was disabled → re-enable with fresh slot
        return {
          ...prev,
          [day]: [{
            id: null,
            agent_id: user.id,
            day_of_week: day,
            start_time: '09:00:00',
            end_time: '17:00:00',
            is_active: 1
          }]
        };
      }

      // Add another slot to active day
      return {
        ...prev,
        [day]: [
          ...activeSlots,
          {
            id: null,
            agent_id: user.id,
            day_of_week: day,
            start_time: '09:00:00',
            end_time: '17:00:00',
            is_active: 1
          }
        ]
      };
    });
  };

  const removeTimeSlot = (day, index) => {
    setWeeklySchedule(prev => {
      // Only count active slots
      const activeSlots = (prev[day] || []).filter(s => s.is_active == 1);

      if (activeSlots.length <= 1) {
        // ✅ FIX: Last slot removed → mark day as disabled (day off)
        return {
          ...prev,
          [day]: [{
            id: null,
            agent_id: user.id,
            day_of_week: day,
            start_time: '09:00:00',
            end_time: '17:00:00',
            is_active: 0
          }]
        };
      }

      // Remove the specific slot
      return {
        ...prev,
        [day]: activeSlots.filter((_, i) => i !== index)
      };
    });
  };

  const updateTimeSlot = (day, index, field, value) => {
    setWeeklySchedule(prev => {
      const activeSlots = (prev[day] || []).filter(s => s.is_active == 1);
      return {
        ...prev,
        [day]: activeSlots.map((slot, i) => 
          i === index ? { ...slot, [field]: value } : slot
        )
      };
    });
  };

  const addSpecificDate = () => {
    setSpecificDates(prev => [
      ...prev,
      {
        id: null,
        agent_id: user.id,
        specific_date: '',
        start_time: '09:00:00',
        end_time: '17:00:00',
        is_active: 1
      }
    ]);
  };

  const removeSpecificDate = (index) => {
    setSpecificDates(prev => prev.filter((_, i) => i !== index));
  };

  const updateSpecificDate = (index, field, value) => {
    setSpecificDates(prev => prev.map((date, i) => 
      i === index ? { ...date, [field]: value } : date
    ));
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏰</div>
          <div>جاري التحميل...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '25px',
          borderRadius: '12px',
          marginBottom: '25px',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px' }}>🕐 Set Your Availability</h1>
            <p style={{ margin: '5px 0 0 0', opacity: 0.9 }}>
              Choose between recurring weekly availability or specific dates
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '10px 20px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid white',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}>
            ← Back to Dashboard
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '20px' }}>
            
            {/* Main Content */}
            <div>
              
              {/* Weekly Availability */}
              <div style={{
                backgroundColor: 'rgba(26, 32, 56, 0.8)',
                padding: '25px',
                borderRadius: '12px',
                marginBottom: '20px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h3 style={{ margin: '0 0 5px 0', fontSize: '18px', color: '#f9fafb', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  📅 Weekly Availability
                </h3>
                <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#9ca3af' }}>
                  Set your regular weekly availability that repeats every week
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                  {daysEnglish.map(day => {
                    const slots = weeklySchedule[day] || [];
                    const activeSlots = slots.filter(s => s.is_active == 1);
                    const isDayActive = activeSlots.length > 0;

                    return (
                      <div key={day} style={{
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        padding: '15px',
                        opacity: isDayActive ? 1 : 0.5,
                        transition: 'opacity 0.3s ease'
                      }}>
                        <div style={{ 
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: isDayActive ? '12px' : '0'
                        }}>
                          <span style={{
                            fontSize: '15px', 
                            fontWeight: '600', 
                            color: '#f9fafb'
                          }}>
                            {daysArabic[day]}
                          </span>
                          {!isDayActive && (
                            <span style={{ fontSize: '12px', color: '#ef4444' }}>
                              يوم إجازة
                            </span>
                          )}
                        </div>

                        {activeSlots.map((slot, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            gap: '8px', 
                            marginBottom: '8px',
                            alignItems: 'center'
                          }}>
                            <input
                              type="time"
                              value={slot.start_time}
                              onChange={(e) => updateTimeSlot(day, index, 'start_time', e.target.value)}
                              style={{
                                flex: 1,
                                padding: '8px',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '4px',
                                backgroundColor: '#1a2038',
                                color: '#f9fafb',
                                fontSize: '13px'
                              }}
                            />
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>to</span>
                            <input
                              type="time"
                              value={slot.end_time}
                              onChange={(e) => updateTimeSlot(day, index, 'end_time', e.target.value)}
                              style={{
                                flex: 1,
                                padding: '8px',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '4px',
                                backgroundColor: '#1a2038',
                                color: '#f9fafb',
                                fontSize: '13px'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeTimeSlot(day, index)}
                              style={{
                                padding: '6px 8px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}>
                              ✕
                            </button>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={() => addTimeSlot(day)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: 'rgba(102, 126, 234, 0.2)',
                            color: '#a5b4fc',
                            border: '1px solid rgba(102, 126, 234, 0.3)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            marginTop: isDayActive ? '5px' : '12px'
                          }}>
                          + Add Time Slot
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Specific Dates */}
              <div style={{
                backgroundColor: 'rgba(26, 32, 56, 0.8)',
                padding: '25px',
                borderRadius: '12px',
                marginBottom: '20px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h3 style={{ margin: '0 0 5px 0', fontSize: '18px', color: '#f9fafb', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  📅 Specific Dates
                </h3>
                <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#9ca3af' }}>
                  Set availability for specific dates (one-time events)
                </p>

                {specificDates.length === 0 ? (
                  <div style={{
                    padding: '30px',
                    textAlign: 'center',
                    color: '#9ca3af',
                    fontSize: '14px'
                  }}>
                    No specific dates added yet
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {specificDates.map((date, index) => (
                      <div key={index} style={{
                        display: 'grid',
                        gridTemplateColumns: '180px 1fr 1fr auto',
                        gap: '10px',
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        borderRadius: '6px'
                      }}>
                        <input
                          type="date"
                          value={date.specific_date}
                          onChange={(e) => updateSpecificDate(index, 'specific_date', e.target.value)}
                          style={{
                            padding: '8px',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '4px',
                            backgroundColor: '#1a2038',
                            color: '#f9fafb',
                            fontSize: '13px'
                          }}
                        />
                        <input
                          type="time"
                          value={date.start_time}
                          onChange={(e) => updateSpecificDate(index, 'start_time', e.target.value)}
                          style={{
                            padding: '8px',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '4px',
                            backgroundColor: '#1a2038',
                            color: '#f9fafb',
                            fontSize: '13px'
                          }}
                        />
                        <input
                          type="time"
                          value={date.end_time}
                          onChange={(e) => updateSpecificDate(index, 'end_time', e.target.value)}
                          style={{
                            padding: '8px',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '4px',
                            backgroundColor: '#1a2038',
                            color: '#f9fafb',
                            fontSize: '13px'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removeSpecificDate(index)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}>
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={addSpecificDate}
                  style={{
                    marginTop: '15px',
                    padding: '10px 20px',
                    backgroundColor: 'rgba(102, 126, 234, 0.2)',
                    color: '#a5b4fc',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                  + Add Another Date
                </button>
              </div>

              {/* Time Zone */}
              <div style={{
                backgroundColor: 'rgba(26, 32, 56, 0.8)',
                padding: '25px',
                borderRadius: '12px',
                marginBottom: '20px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#f9fafb' }}>
                  🌍 Time Zone
                </h3>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '6px',
                    backgroundColor: '#1a2038',
                    color: '#f9fafb',
                    fontSize: '14px'
                  }}>
                  {timezones.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
                <div style={{ marginTop: '10px', fontSize: '13px', color: '#9ca3af' }}>
                  Current Time: {new Date().toLocaleString('en-US', { timeZone: timezone })}
                </div>
              </div>

              {/* Save Buttons */}
              <div style={{ display: 'flex', gap: '15px' }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '14px',
                    backgroundColor: saving ? '#6c757d' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}>
                  {saving ? '⏳ Saving...' : '💾 Save All Availability'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  style={{
                    padding: '14px 24px',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: '#d1d5db',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}>
                  ✕ Cancel
                </button>
              </div>
            </div>

            {/* Tips Sidebar */}
            <div>
              <div style={{
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                border: '2px solid rgba(102, 126, 234, 0.3)',
                borderRadius: '12px',
                padding: '20px',
                position: 'sticky',
                top: '20px'
              }}>
                <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#a5b4fc', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  💡 Tips
                </h3>
                
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#f9fafb' }}>
                    Weekly vs Specific Dates
                  </h4>
                  <p style={{ margin: 0, fontSize: '13px', color: '#d1d5db', lineHeight: '1.5' }}>
                    Use weekly availability for regular meetings and specific dates for one-time events.
                  </p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#f9fafb' }}>
                    Multiple Time Slots
                  </h4>
                  <p style={{ margin: 0, fontSize: '13px', color: '#d1d5db', lineHeight: '1.5' }}>
                    Add multiple time slots per day for flexibility.
                  </p>
                </div>

                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#f9fafb' }}>
                    Time Zone
                  </h4>
                  <p style={{ margin: 0, fontSize: '13px', color: '#d1d5db', lineHeight: '1.5' }}>
                    Your availability will be shown to clients in their local time zone.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default Availability;